import { GridColDef } from '@mui/x-data-grid-premium';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ButtonCellComponent,
  EmployeeCellComponent,
  dateCellValueFormatter,
  getSingleSelectColumnEnumValueOptions,
} from '~/components/SolaForceDataGrid';
import { useAllEmployees } from '~/data/employees';
import { EnumListKey } from '~/data/enums';
import { WorkflowEmploymentDetailsReportData } from '~/data/reports';
import { useReportNavigateToView } from '~/scenes/PortalModules/Analytics/common';
import { convertToDateOrUndefined } from '~/utils/helpers/date';
import { getEnumName } from '~/utils/helpers/enum';

import {
  EmploymentDetailsReportRow,
  WorkflowReportHookOutput,
  WorkflowReportHookProps,
} from '../../types';
import { DefaultChangedCell } from '../components/ChangedCellsFormatter/DefaultCell';
import { StartEndDateChangedCell } from '../components/ChangedCellsFormatter/StartEndDate';
import { DateHistoryCell } from '../components/HistoryCellsFormatter/DateHistoryCell';
import { HistoryCell } from '../components/HistoryCellsFormatter/DefaultHistoryCell';
import { buildProposalValueChanges } from '../utils/buildProposalValueChanges';
import { mapWorkflowHistory } from '../utils/mapWorkflowHistory';

import { resolveEmploymentTranslatedData } from './resolveEmploymentTranslatedData';
import { usePersonnelGroupEnums } from './usePersonnelGroupEnums';

export const useEmploymentDetailsReport = (
  props: WorkflowReportHookProps<WorkflowEmploymentDetailsReportData>,
): WorkflowReportHookOutput<EmploymentDetailsReportRow> => {
  const { data, cellRenderState } = props;
  const { showChanges, showHistory } = cellRenderState;
  const { t } = useTranslation();
  const { goToEmployeeBasicDataView } = useReportNavigateToView();
  const { getEmployeeById } = useAllEmployees();
  const { personnelGroupNames, personnelGroupEnumsByGroup } =
    usePersonnelGroupEnums();

  const rows = useMemo(() => {
    return data.flatMap((dataEntry) => {
      const { fEmpId, fEmpDetails } = dataEntry;

      return dataEntry.fProposals.map(
        (proposal): EmploymentDetailsReportRow => {
          const { fProposal, fReferenceData, fWorkflowEntries } = proposal;

          const workflowHistory = mapWorkflowHistory(
            fWorkflowEntries,
            getEmployeeById,
          );

          const proposalData = resolveEmploymentTranslatedData(
            fProposal.translate,
            personnelGroupEnumsByGroup,
          );

          const referenceData = fReferenceData?.translate
            ? resolveEmploymentTranslatedData(
                fReferenceData.translate,
                personnelGroupEnumsByGroup,
              )
            : undefined;

          const changes = referenceData
            ? buildProposalValueChanges(referenceData, proposalData)
            : undefined;

          return {
            employeeId: fEmpId,
            firstName: fEmpDetails.fEmpFirstName,
            lastName: fEmpDetails.fEmpLastName,
            employeeNumber: fEmpDetails.fEmpPersonNumber,
            ...proposalData,
            startDate: convertToDateOrUndefined(proposalData.startDate),
            endDate: convertToDateOrUndefined(proposalData.endDate),
            workflowHistory,
            changes,
          };
        },
      );
    });
  }, [data, getEmployeeById, personnelGroupEnumsByGroup]);

  const columns = useMemo(
    (): GridColDef<EmploymentDetailsReportRow>[] => [
      {
        field: 'employeeId',
        headerName: t('laPicture'),
        filterable: false,
        sortable: false,
        disableExport: true,
        minWidth: 30,
        renderCell: (params) => (
          <EmployeeCellComponent
            {...params}
            pictureOnly={true}
            onClick={() => goToEmployeeBasicDataView(params.row.employeeId)}
          />
        ),
      },
      {
        field: 'firstName',
        headerName: t('laFirstName'),
        hideable: false,
        minWidth: 90,
        renderCell: (params) => (
          <ButtonCellComponent
            {...params}
            onClick={() => goToEmployeeBasicDataView(params.row.employeeId)}
          />
        ),
      },
      {
        field: 'lastName',
        headerName: t('laLastName'),
        hideable: false,
        minWidth: 110,
        renderCell: (params) => (
          <ButtonCellComponent
            {...params}
            onClick={() => goToEmployeeBasicDataView(params.row.employeeId)}
          />
        ),
      },
      {
        field: 'employeeNumber',
        headerName: t('laEmpId'),
        minWidth: 130,
      },
      {
        field: 'employmentStatus',
        headerName: t('laEmploymentStatus'),
        minWidth: 150,
        renderCell: (params) =>
          DefaultChangedCell({
            params,
            showChanges,
          }),
      },
      ...[
        EnumListKey.PERSONNEL_GROUP_1,
        EnumListKey.PERSONNEL_GROUP_2,
        EnumListKey.PERSONNEL_GROUP_3,
        EnumListKey.PERSONNEL_GROUP_4,
        EnumListKey.PERSONNEL_GROUP_5,
      ].map(
        (key, index): GridColDef<EmploymentDetailsReportRow> => ({
          field: `personnelGroup${index + 1}`,
          headerName: getEnumName(personnelGroupNames, key),
          type: 'singleSelect',
          valueOptions: getSingleSelectColumnEnumValueOptions(
            personnelGroupEnumsByGroup[key] ?? [],
          ),
          flex: 1,
          minWidth: 140,
          renderCell: (params) =>
            DefaultChangedCell({
              params,
              showChanges,
            }),
        }),
      ),
      {
        field: 'startDate',
        headerName: t('laStartDate'),
        minWidth: 120,
        valueFormatter: dateCellValueFormatter,
        renderCell: (params) =>
          StartEndDateChangedCell({
            params,
            showChanges,
          }),
      },
      {
        field: 'endDate',
        headerName: t('laEndDate'),
        minWidth: 120,
        valueFormatter: dateCellValueFormatter,
        renderCell: (params) =>
          StartEndDateChangedCell({
            params,
            showChanges,
          }),
      },
      {
        field: 'info',
        headerName: t('laInfo'),
        minWidth: 150,
        renderCell: (params) =>
          DefaultChangedCell({
            params,
            showChanges,
          }),
      },
      {
        field: 'jobGrade',
        headerName: t('laJobGrade'),
        minWidth: 150,
        renderCell: (params) =>
          DefaultChangedCell({
            params,
            showChanges,
          }),
      },
      {
        field: 'taxNumber',
        headerName: t('laTaxNumber'),
        minWidth: 150,
        renderCell: (params) => DefaultChangedCell({ params, showChanges }),
      },
      {
        field: 'vacationBalanceCalculation',
        headerName: t('fEmplDetailVacBalCalcSixDays'),
        minWidth: 200,
        renderCell: (params) =>
          DefaultChangedCell({
            params,
            showChanges,
          }) + ` ${t('laDays')}`,
      },
      {
        field: 'calculateHoursBalance',
        headerName: t('fEmplDetailCalcHoursBalance'),
        minWidth: 200,
        valueGetter: (value) => (value ? t('laYes') : t('laNo')),
        renderCell: (params) =>
          DefaultChangedCell({
            params,
            showChanges,
          }),
      },
      {
        field: 'scheduledDate',
        headerName: t('laScheduledDate'),
        minWidth: 150,
        renderCell: (params) => (
          <DateHistoryCell
            workflowHistory={params.row.workflowHistory}
            showHistory={showHistory}
            fieldKey="scheduledDate"
          />
        ),
      },
      {
        field: 'updatedAt',
        headerName: t('Updated'),
        minWidth: 170,
        renderCell: (params) => (
          <DateHistoryCell
            workflowHistory={params.row.workflowHistory}
            showHistory={showHistory}
            fieldKey="updatedAt"
            format="DD/MM/YYYY HH:mm:ss"
          />
        ),
      },
      {
        field: 'status',
        headerName: t('laStatus'),
        minWidth: 150,
        renderCell: (params) => (
          <HistoryCell
            workflowHistory={params.row.workflowHistory}
            showHistory={showHistory}
            fieldKey="status"
          />
        ),
      },
      {
        field: 'approvedBy',
        headerName: t('laApprovedBy'),
        minWidth: 150,
        renderCell: (params) => (
          <HistoryCell
            workflowHistory={params.row.workflowHistory}
            showHistory={showHistory}
            fieldKey="approvedBy"
          />
        ),
      },
    ],
    [
      t,
      goToEmployeeBasicDataView,
      showHistory,
      showChanges,
      personnelGroupEnumsByGroup,
      personnelGroupNames,
    ],
  );

  return { rows, columns };
};
